import React, { useState } from 'react';
import { Upload, FileText, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ModernSettingHeader from '@/components/settings/ModernSettingHeader';
import { toastMessage } from '@/utils/toast-message';
import api from '@/utils/api';

interface ImportStats {
  leads_created: number;
  leads_linked: number;
  call_logs_created: number;
  duplicates_skipped: number;
  errors: string[];
}

const ImportKnowlarity: React.FC = () => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [importResult, setImportResult] = useState<ImportStats | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setSelectedFile(file);
        setImportResult(null);
      } else {
        toastMessage('Please select a CSV file', 'error');
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const file = e.dataTransfer.files[0];
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv'))) {
      setSelectedFile(file);
      setImportResult(null);
    } else {
      toastMessage('Please drop a CSV file', 'error');
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toastMessage('Please select a file first', 'warning');
      return;
    }

    setIsUploading(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('csvFile', selectedFile);

      const response = await api.req({
        url: '/import/knowlarity',
        method: api.Method.POST,
        body: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.success) {
        setImportResult(response.data as ImportStats);
        toastMessage(response.message || 'Import completed successfully', 'success');
      } else {
        toastMessage(response.message || 'Import failed', 'error');
      }
    } catch (error: any) {
      console.error('Import error:', error);
      toastMessage(error.response?.data?.message || 'Failed to import file', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setImportResult(null);
  };

  return (
    <div className="h-full flex flex-col">
      <ModernSettingHeader
        title="Import Knowlarity Leads"
        description="Upload Knowlarity call logs CSV to import leads and call history"
        icon={<Upload className="w-5 h-5" />}
      />

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl space-y-6">
          {/* Instructions Card */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Instructions
            </h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800">
              <li>Export call logs from your Knowlarity dashboard as CSV</li>
              <li>The CSV should contain columns: Call UUID, Date and Time, Customer, Agent Name, etc.</li>
              <li>Upload the CSV file using the upload area below</li>
              <li>Click "Import" to process the file</li>
            </ol>
            <div className="mt-4 p-3 bg-blue-100 rounded">
              <p className="text-sm font-medium text-blue-900 mb-1">What happens during import:</p>
              <ul className="list-disc list-inside text-xs text-blue-800 space-y-1">
                <li>New leads will be created for unique phone numbers (Source: Phone, Status: New Lead)</li>
                <li>Existing leads will be linked to call logs</li>
                <li>Call recordings will be added as remarks</li>
                <li>Duplicate call UUIDs will be skipped</li>
              </ul>
            </div>
          </div>

          {/* Upload Card */}
          {!importResult && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold mb-4">Upload CSV File</h3>

              {/* File Upload Area */}
              <div
                className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                  isDragOver
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {selectedFile ? (
                  <div className="space-y-4">
                    <FileText className="w-16 h-16 mx-auto text-green-500" />
                    <div>
                      <p className="text-lg font-medium text-gray-900">{selectedFile.name}</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {(selectedFile.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                    <div className="flex justify-center gap-3">
                      <button
                        onClick={handleImport}
                        disabled={isUploading}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {isUploading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            <span>Importing...</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            <span>Import</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleReset}
                        disabled={isUploading}
                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Change File
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Upload className="w-16 h-16 mx-auto text-gray-400" />
                    <div>
                      <p className="text-lg font-medium text-gray-900">
                        Drop your CSV file here
                      </p>
                      <p className="text-sm text-gray-500 mt-1">or</p>
                    </div>
                    <label className="inline-block">
                      <input
                        type="file"
                        accept=".csv,text/csv"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      <span className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer inline-flex items-center gap-2">
                        <Upload className="w-4 h-4" />
                        Browse Files
                      </span>
                    </label>
                    <p className="text-xs text-gray-500">CSV files only, max 10MB</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Results Card */}
          {importResult && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <CheckCircle className="w-6 h-6 text-green-500" />
                  Import Completed
                </h3>
                <button
                  onClick={handleReset}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Import Another File
                </button>
              </div>

              {/* Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-green-600 font-medium">Leads Created</p>
                  <p className="text-2xl font-bold text-green-900 mt-1">
                    {importResult.leads_created}
                  </p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-600 font-medium">Leads Linked</p>
                  <p className="text-2xl font-bold text-blue-900 mt-1">
                    {importResult.leads_linked}
                  </p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-sm text-purple-600 font-medium">Call Logs</p>
                  <p className="text-2xl font-bold text-purple-900 mt-1">
                    {importResult.call_logs_created}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 font-medium">Duplicates Skipped</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {importResult.duplicates_skipped}
                  </p>
                </div>
              </div>

              {/* Errors */}
              {importResult.errors && importResult.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <XCircle className="w-5 h-5 text-red-500" />
                    <h4 className="font-semibold text-red-900">
                      Errors ({importResult.errors.length})
                    </h4>
                  </div>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {importResult.errors.map((error, index) => (
                      <p key={index} className="text-sm text-red-700">
                        • {error}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-center gap-3 mt-6 pt-6 border-t">
                <button
                  onClick={() => navigate('/leads')}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  View Leads
                </button>
                <button
                  onClick={() => navigate('/settings')}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Back to Settings
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportKnowlarity;
